import { differenceInDays, subDays, subMonths } from "date-fns";
import { getIntlLocale, translate, translatePlural } from "@/i18n/translate";

export interface DatePeriodInfo {
  periodName: string; // "day", "week", "month", "period"
  /** shown in the UI, in the current language: "This week", "Past 3 days", "Oct 1 – Dec 15" */
  periodLabel: string;
  // internal English descriptions, not shown in the UI
  comparisonLabel: string; // "compared to previous week", "week-over-week", etc.
  shortLabel: string; // "week", "month", "period"
  previousPeriod: { start: Date; end: Date };
}

/** "Oct 1" / "1. 10." */
const formatDayMonth = (date: Date) =>
  date.toLocaleDateString(getIntlLocale(), { day: "numeric", month: "short" });

/**
 * Analyzes a date range and returns appropriate period labels and comparison periods.
 * The period label is in the current UI language: call it again when the language changes.
 */
export function analyzeDatePeriod(start: Date, end: Date): DatePeriodInfo {
  const daysDiff = differenceInDays(end, start) + 1; // +1 to include both start and end dates

  // Determine the most appropriate period name
  let periodName: string;
  let periodLabel: string;
  let comparisonLabel: string;
  let shortLabel: string;
  let previousPeriod: { start: Date; end: Date };

  if (daysDiff === 1) {
    // Single day
    periodName = "day";
    periodLabel = translate("common.today");
    shortLabel = "day";
    comparisonLabel = "compared to previous day";

    const prevEnd = subDays(start, 1);
    const prevStart = subDays(start, 1);
    previousPeriod = { start: prevStart, end: prevEnd };
  } else if (daysDiff <= 7) {
    // Week or less
    periodName = "week";
    periodLabel =
      daysDiff === 7
        ? translate("dashboard.period.thisWeek")
        : translatePlural("dashboard.period.pastDays", daysDiff);
    shortLabel = daysDiff === 7 ? "week" : `${daysDiff} days`;
    comparisonLabel =
      daysDiff === 7
        ? "compared to previous week"
        : `compared to previous ${daysDiff} days`;

    // Calculate previous period with same duration
    const duration = daysDiff - 1; // Convert to duration in days
    const prevEnd = subDays(start, 1);
    const prevStart = subDays(prevEnd, duration);
    previousPeriod = { start: prevStart, end: prevEnd };
  } else if (daysDiff <= 31) {
    // Month or less
    const weeks = Math.round(daysDiff / 7);

    if (weeks <= 4) {
      periodName = "week";
      periodLabel = translatePlural("dashboard.period.pastWeeks", weeks);
      shortLabel = `${weeks} weeks`;
      comparisonLabel = `compared to previous ${weeks} weeks`;

      // Calculate previous period with same duration
      const duration = daysDiff - 1;
      const prevEnd = subDays(start, 1);
      const prevStart = subDays(prevEnd, duration);
      previousPeriod = { start: prevStart, end: prevEnd };
    } else {
      periodName = "month";
      periodLabel = translate("dashboard.period.thisMonth");
      shortLabel = "month";
      comparisonLabel = "compared to previous month";

      const prevEnd = subDays(start, 1);
      const prevStart = subMonths(start, 1);
      previousPeriod = { start: prevStart, end: prevEnd };
    }
  } else {
    // Longer periods - use months
    const months = Math.round(daysDiff / 30);

    if (months <= 3) {
      periodName = "month";
      periodLabel = translatePlural("dashboard.period.pastMonths", months);
      shortLabel = `${months} months`;
      comparisonLabel = `compared to previous ${months} months`;
    } else {
      periodName = "period";
      periodLabel = `${formatDayMonth(start)} – ${formatDayMonth(end)}`;
      shortLabel = "period";
      comparisonLabel = "compared to previous period";
    } // Calculate previous period with same duration
    const duration = daysDiff - 1;
    const prevEnd = subDays(start, 1);
    const prevStart = subDays(prevEnd, duration);
    previousPeriod = { start: prevStart, end: prevEnd };
  }

  return {
    periodName,
    periodLabel,
    comparisonLabel,
    shortLabel,
    previousPeriod,
  };
}

/**
 * Gets trend description text based on the period info and trend
 */
export function getTrendDescription(
  trend: number,
  periodInfo: DatePeriodInfo,
): string {
  if (trend === 0) {
    return `No change from ${periodInfo.comparisonLabel.replace("compared to ", "")}`;
  }

  const direction = trend > 0 ? "Up" : "Down";
  const absChange = Math.abs(trend).toFixed(1);

  return `${direction} ${absChange}% from ${periodInfo.comparisonLabel.replace("compared to ", "")}`;
}
